import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { Router, RouterModule } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import {
  catchError,
  distinctUntilChanged,
  Observable,
  of,
  switchMap
} from 'rxjs';
import { VehiclesService } from 'src/app/core/services/vehicles.service';
import { StorageService } from 'src/app/core/storage/storage.service';
import { RequestType } from 'src/app/shared/model/requestType.enum';
import { Translate } from 'src/app/shared/utils/translate';

@Component({
  standalone: true,
  selector: 'app-step-vehicle-finder',
  templateUrl: './step-vehicle-finder.component.html',
  styleUrls: ['./step-vehicle-finder.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    I18NextModule,
    FormsModule,
    MatStepperModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatAutocompleteModule
  ]
})
export class StepVehicleFinderComponent implements OnInit {
  searchForm: FormGroup;
  searchRedPlateForm: FormGroup;
  plateNumber = '';
  vin = '';
  searchError = false;
  vinRepeat = false;
  vehicles: any[] = [];
  inspectionReport = false;
  requestTypeTransfer = '';
  requestTypeEnum = RequestType;
  searchRedPlateControl = new FormControl('', [Validators.minLength(3)]);
  redPlates: Observable<string[]> = of([]);
  redPlateSelected = '';
  truckPlateRequired = false;

  // booleans autocomplete
  isSelected = false;
  redPlateError = false;
  noRedPlateError = false;

  @Input() requestType!: string;
  @Input() stepper!: MatStepper;

  @Output() vehicleFinderEmitter = new EventEmitter<{
    vins?: string[];
    redPlate?: string;
    truckPlateNumber?: string;
  }>();
  @Output() backStepEmitter = new EventEmitter();

  landing2 = false;

  constructor(
    private fb: FormBuilder,
    private vehicleService: VehiclesService,
    private router: Router,
    private storageService: StorageService,
    public translate: Translate
  ) {
    this.searchForm = this.fb.group({
      vin: [''],
      plateNumber: [''],
      truckPlateNumber: ['']
    });
    this.searchRedPlateForm = this.fb.group({
      redPlate: ['']
    });
  }

  ngOnInit(): void {
    this.landing2 = this.storageService.get('landing2') || false;
    this.setupFormListeners();
    this.getAutocomplete();
  }

  setupFormListeners() {
    const vinControl = this.searchForm.get('vin');
    const plateNumberControl = this.searchForm.get('plateNumber');

    vinControl?.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      this.vin = value;
      if (value) {
        plateNumberControl?.disable({ emitEvent: false });
      } else {
        plateNumberControl?.enable({ emitEvent: false });
      }
    });

    plateNumberControl?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value) => {
        this.plateNumber = value;
        if (value) {
          vinControl?.disable({ emitEvent: false });
        } else {
          vinControl?.enable({ emitEvent: false });
        }
      });
  }

  getAutocomplete() {
    this.redPlates = this.searchRedPlateControl.valueChanges.pipe(
      switchMap((value) => {
        if (value!.length >= 3) {
          return this.vehicleService.getVehiclesRedPlate(value!).pipe(
            catchError(() => {
              this.isSelected = false;
              this.redPlateError = true;
              return of([]);
            }),
            switchMap((redPlates: string[]) => {
              this.isSelected = false;

              if (redPlates.length === 0 && !this.isSelected) {
                // Si no hay coincidencias
                this.redPlateError = true;
                this.noRedPlateError = false;
                return of(redPlates);
              } else if (
                redPlates.length > 0 &&
                !this.isSelected &&
                this.redPlateSelected !== this.searchRedPlateControl.value
              ) {
                this.noRedPlateError = true;
                this.redPlateError = false;
                return of(redPlates);
              } else {
                this.redPlateError = false;
                this.noRedPlateError = false;
                return of(redPlates);
              }
            })
          );
        } else {
          return of([]);
        }
      })
    );
  }

  onSearch() {
    const vin = this.searchForm.get('vin')?.value;
    const plateNumber = this.searchForm.get('plateNumber')?.value;
    if ((vin || plateNumber) && this.requestType === this.requestTypeEnum.es) {
      this.vehicleService.getVehiclesVinOrPlateES(vin, plateNumber).subscribe(
        (vehicle) => {
          this.searchError = false;
          const existingVehicle = this.vehicles.find(
            (v) => v.vin === vehicle.vin
          );
          if (existingVehicle) {
            this.vinRepeat = true;
          } else {
            this.vehicles.push(vehicle);
            this.vinRepeat = false;
            this.searchForm.reset();
            const truckPlateControl = this.searchForm.get('truckPlateNumber');
            if (this.vehicles.length > 1 && !!truckPlateControl) {
              this.truckPlateRequired = true;
              truckPlateControl.setValidators([Validators.required]);
            } else if (!!truckPlateControl) {
              this.truckPlateRequired = false;
              truckPlateControl.clearValidators();
              truckPlateControl.updateValueAndValidity();
            }
          }
        },
        (error) => {
          console.error('Error fetching vehicles:', error);
          this.searchError = true;
          this.inspectionReport = false;
        }
      );
    } else if (
      (vin || plateNumber) &&
      this.requestType !== this.requestTypeEnum.es
    ) {
      this.vehicleService.getVehiclesVinOrPlate(vin, plateNumber).subscribe(
        (vehicle) => {
          this.searchError = false;
          const existingVehicle = this.vehicles.find(
            (v) => v.vehicle.vin === vehicle.vehicle.vin
          );
          if (
            vehicle.inspectionReport &&
            this.requestType === RequestType.judgements
          ) {
            this.inspectionReport = vehicle.inspectionReport;
          } else {
            if (this.requestType === RequestType.judgements) {
              this.inspectionReport = vehicle.inspectionReport;
            }
            if (existingVehicle) {
              this.vinRepeat = true;
            } else {
              this.vehicles.push(vehicle);
              this.vinRepeat = false;
              this.searchForm.reset();
              const truckPlateControl = this.searchForm.get('truckPlateNumber');
              if (!!truckPlateControl) {
                this.truckPlateRequired = false;
                truckPlateControl.clearValidators();
                truckPlateControl.updateValueAndValidity();
              }
              if (this.requestType === RequestType.judgements) {
                this.searchForm.get('vin')?.disable();
                this.searchForm.get('plateNumber')?.disable();
              }
            }
          }
        },
        (error) => {
          console.error('Error fetching vehicles:', error);
          this.searchError = true;
          this.inspectionReport = false;
        }
      );
    }
  }

  removeVehicle(index: number) {
    this.vehicles.splice(index, 1);
    if (this.requestType !== RequestType.transfers) {
      this.searchForm.get('vin')?.enable();
      this.searchForm.get('plateNumber')?.enable();
    }

    const truckPlateControl = this.searchForm.get('truckPlateNumber');
    if (
      this.requestType === this.requestTypeEnum.es &&
      this.vehicles.length > 1 &&
      !!truckPlateControl
    ) {
      this.truckPlateRequired = true;
      truckPlateControl.setValidators([Validators.required]);
    } else if (!!truckPlateControl) {
      this.truckPlateRequired = false;
      truckPlateControl.clearValidators();
      truckPlateControl.updateValueAndValidity();
    }
  }

  onOptionSelected(event: any): void {
    this.redPlateError = false;
    this.isSelected = true;
    this.noRedPlateError = false;
    this.redPlateSelected = event.option.value;
  }

  nextStep() {
    if (this.redPlateSelected) {
      const redPlate = this.redPlateSelected;
      this.vehicleFinderEmitter.emit({ redPlate });
    } else if (this.requestType === this.requestTypeEnum.es) {
      const vins = this.vehicles.map((vehicle) => vehicle.vin);
      const truckPlateNumber = this.searchForm.get('truckPlateNumber')?.value;
      this.vehicleFinderEmitter.emit({ vins, truckPlateNumber });
    } else {
      const vins = this.vehicles.map((vehicle) => vehicle.vehicle.vin);
      this.vehicleFinderEmitter.emit({ vins });
    }
  }
  backStep() {
    this.stepper?.selectedIndex === 0
      ? this.landing2
        ? this.router.navigateByUrl('/landing-leasing')
        : this.router.navigateByUrl('/menu')
      : this.backStepEmitter.emit();
  }
}
